import {useContext, useMemo} from "react";
import {Stack, Text} from "@chakra-ui/react";
import {UserGroupIcon} from "@heroicons/react/20/solid";
import {Link as RouterLink} from "react-router-dom";
import type {ReviewerRole, ReviewerRoleId} from "platformed-client/types";
import {q, useQueriesData} from "../../../state";
import {ProductContext} from "../../SharedPages/ProductContext";
import {Button} from "../../../components/ui/button";
import {EmptyState} from "../../../components/ui/empty-state";
import {RoleSection} from "./RoleSection";
import {ReviewerRow} from "./ReviewerRow";
import {groupByRole, type ReviewMap} from "./reviewsShared";
import {unassignedReviews, type ApplyOutcome, type ApplyPlan, type ContentId} from "./reviewCandidates";
import {useCreateReviews} from "./useCreateReviews";
import type {ReviewItem} from ".";
import T from "platformed-components/i18n/T";

export type RoleReviewSectionsProps = {
  reviews: ReviewMap;
  roles: ReviewerRole[];
  reviewItem: ReviewItem;
  /** Pages beneath this one, for the cascade options. 0 hides them. */
  descendantPageCount?: number;
  /** Changes queued but not yet written, keyed by role. Empty when the surface saves as it goes. */
  staged?: Map<ReviewerRoleId, ApplyPlan>;
  onApply: (plan: ApplyPlan) => Promise<ApplyOutcome>;
};

/**
 * The reviewer list, grouped by role, with per-role assignment. Callers differ only in whether
 * `onApply` writes each change straight away or queues it.
 */
export const RoleReviewSections = ({
  reviews,
  roles,
  reviewItem,
  descendantPageCount = 0,
  staged,
  onApply,
}: RoleReviewSectionsProps) => {
  const product = useContext(ProductContext);
  const [accountUsers, teams] = useQueriesData({queries: [q.accountUsers(product), q.teams()]});

  const owners = useMemo(
    () => [
      ...(accountUsers ?? []).map(({user}) => ({type: "User" as const, content: user})),
      ...(teams ?? []).map(team => ({type: "Team" as const, content: team})),
    ],
    [accountUsers, teams],
  );

  /** Who each queued plan would put into which role, so a picker can block the other roles'
      queued additions — the server can't report a change that hasn't been written. */
  const stagedRoles = useMemo(() => {
    const byContentId = new Map<ContentId, ReviewerRole>();
    for (const plan of staged?.values() ?? []) {
      const role = roles.find(r => r.reviewer_role_id === plan.roleId);
      if (!role) continue;
      for (const candidate of plan.added) byContentId.set(candidate.contentId, role);
    }
    return byContentId;
  }, [staged, roles]);

  const groups = groupByRole(reviews, roles);
  const orphans = unassignedReviews(reviews, roles);

  return (
    <Stack gap={5}>
      {roles.length === 0 && <NoRoles />}
      {groups.map(group => (
        <RoleSection
          key={group.roleId}
          role={roles.find(r => r.reviewer_role_id === group.roleId)!}
          reviews={group.reviews}
          allReviews={reviews}
          roles={roles}
          owners={owners}
          reviewItem={reviewItem}
          descendantPageCount={descendantPageCount}
          staged={staged?.get(group.roleId)}
          stagedRoles={stagedRoles}
          onApply={onApply}
        />
      ))}

      {orphans.length > 0 && <OtherReviewers reviews={orphans} reviewItem={reviewItem} />}
    </Stack>
  );
};

const NoRoles = () => (
  <EmptyState title={<T keyName="c.No_roles_yet" />} icon={<UserGroupIcon />}>
    <Button asChild colorPalette="green">
      <RouterLink to="/tprm/reviewer-roles">
        <T keyName="c.Define_reviewer_roles" />
      </RouterLink>
    </Button>
  </EmptyState>
);

/**
 * Reviewers no configured role claims — added from a role-less surface, or left behind when a
 * role was deleted. They appear in no picker, so this is the only way to reach them.
 */
const OtherReviewers = ({
  reviews,
  reviewItem,
}: {
  reviews: ReturnType<typeof unassignedReviews>;
  reviewItem: ReviewItem;
}) => {
  const createReviews = useCreateReviews(reviewItem);

  return (
    <Stack gap={1}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wide"
        pb={1.5}
        borderBottomWidth="1px"
        borderColor="border"
      >
        <T keyName="c.Other_reviewers" />
      </Text>
      {reviews.map(review => (
        <ReviewerRow
          key={review.reviewer.owner_id}
          review={review}
          reviewItem={reviewItem}
          onRemove={() =>
            createReviews([
              {
                review_status: "Unrequested",
                reviewer_id: review.reviewer.owner_id,
                reviewer_role_id: review.reviewer_role_id ?? undefined,
              },
            ])
          }
        />
      ))}
    </Stack>
  );
};
